import { useQuery } from '@tanstack/react-query'
import { compact, take } from 'lodash'
import moment from 'moment/moment'
import { useMemo, useState } from 'react'

import { useTransientEventBadge } from '../../hooks/useTransientEventBadge'
import PencilChecklistPersonImageSrc from '../../images/pencil-checklist-person.png'
import { Booking } from '../../models/Booking'
import { ComputeFactory } from '../../models/ComputeFactory'
import { Invoice } from '../../models/Invoice'
import { Item } from '../../models/Item'
import { Line } from '../../models/Line'
import { RecordFactory } from '../../models/RecordFactory'
import { Subaccount } from '../../models/Subaccount'
import { Taxcode } from '../../models/Taxcode'
import { User } from '../../models/User'
import { currencyFormat } from '../../utils/numbers'
import { Icon } from '../Icon'
import { Spinner } from '../Spinner'
import Button from '../app/design/Button'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Modal from '../app/design/Modal'
import Paragraph from '../app/design/Paragraph'
import { ClientSearchableSelect } from '../contacts/ClientSearchableSelect'
import { ContractSearchableSelect } from '../items/ContractSearchableSelect'
import { TypeSearchableSelect } from '../items/TypeSearchableSelect'
import { SelectModal } from '../common/SelectModal'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { Select } from '../common/fields/Select'
import { TextArea } from '../common/fields/TextArea'
import { Receipt, WorkflowPage } from '../workflows/WorkflowPage'
import { DatePicker } from "../common/fields/DatePicker";

type Props = {
    invoice: Invoice
    owner: User
    onUpdateInvoice: (updatedInvoice: Invoice) => void
    items: Item[]
}

export const NewInvoicePage = ({ invoice, owner, onUpdateInvoice, items }: Props) => {
    const [currentEditingExpenseLine, setCurrentEditingExpenseLine] = useState<
        { type: 'new'; line: Line } | { type: 'edit'; line: Line; lineIndex: number } | null
    >(null)

    const [isBookingsSelectModalOpen, setIsBookingsSelectModalOpen] = useState(false)
    const [isNoAppointmentsModalOpen, setIsNoAppointmentsModalOpen] = useState(false)

    const { isBadgeShowing: isItemAddedBadgeShowing, onEvent: onItemAdded } =
        useTransientEventBadge()

    const itemLines = invoice.lines.filter((line) => line.type === 'item')
    const expenseLines = invoice.lines.filter((line) => line.type === 'expense')
    const appointmentLines = invoice.lines.filter((line) => line.type === 'booking')

    const { data: clientBookings, isLoading: areClientBookingsLoading } = useQuery({
        queryKey: ['bookings', invoice.client?.id],
        queryFn: async () => {
            if (invoice.client === undefined || invoice.client.id === undefined) {
                return null
            }
            await RecordFactory.getInstance().fetchBillableBookings(invoice.client.id)
            return ComputeFactory.getInstance().fetchBillableBookings(invoice.client)
        },
    })

    const handleSelectClient = async (user: User | undefined) => {
        const client = await RecordFactory.getInstance().loadClient(user?.id ?? '')
        if (client !== undefined && (client.discount ?? 0) > 0) {
            const updatedInvoiceObj = invoice.clone({
                client: user,
                discount_type: 'percent',
                discount: client.discount,
            })
            if (client.notify) {
                updatedInvoiceObj.channel = client.notify
            }
            onUpdateInvoice(updatedInvoiceObj)
        }
        else {
            const updatedInvoiceObj = invoice.clone({
                client: user,
            })
            if (client?.notify) {
                updatedInvoiceObj.channel = client.notify
            }
            onUpdateInvoice(updatedInvoiceObj)
        }
    }

    const handleSelectSubaccount = (subaccount: Subaccount | undefined) => {
        const updatedInvoice = invoice.clone({ client: subaccount?.client, subaccount })
        onUpdateInvoice(updatedInvoice)
    }

    const itemsSearchableItems = useMemo(() => {
        return items.map((item) => {
            return {
                id: item.id ?? '',
                title: item.name ?? '',
                subtitle: (
                    <>
                        <span className={item.discountAmount !== undefined ? 'line-through' : ''}>
                            {currencyFormat(item.rate)}
                        </span>{' '}
                        {item.rate !== undefined && item.discountAmount !== undefined
                            ? currencyFormat(item.rate - item.discountAmount)
                            : ''}
                    </>
                ),
                tertiaryView:
                    item.iconConfig === undefined ? undefined : item.iconConfig.type === 'image' ? (
                        <img src={item.iconConfig.uri} className="size-9 rounded-full" />
                    ) : (
                        <Icon icon={item.iconConfig.icon} background="round" className="size-9" />
                    ),
                item,
            }
        })
    }, [items])

    const handleSelectItemOption = (itemOption: (typeof itemsSearchableItems)[number] | null) => {
        if (itemOption === null) {
            return
        }

        const itemLine = Line.initWithItem(
            itemOption.item.clone({ quantity: 1, memo: '' }),
            invoice
        )
        itemLine.quantity = 1
        itemLine.total = itemOption.item.rate ?? 0
        itemLine.taxable =
            itemOption.item.type === 'product' && Taxcode.defaultProductTaxable(owner.state)
        itemLine.discountable = itemOption.item.type !== 'product'

        const newSalesTaxInfo = itemLine.taxable
            ? {
                  nexus: 'pro',
                  taxcode: owner.taxcode,
                  tax: owner.taxcode?.rate,
              }
            : {}

        const newLines = [...invoice.lines, itemLine]

        if ((itemOption.item.discount ?? 0) > 0 && invoice.discount === undefined) {
            onUpdateInvoice(
                invoice.clone({
                    lines: newLines,
                    discount_type: itemOption.item.discountType,
                    discount: itemOption.item.discount,
                    ...newSalesTaxInfo,
                })
            )
        }
        else {
            onUpdateInvoice(
                invoice.clone({
                    lines: newLines,
                    ...newSalesTaxInfo,
                })
            )
        }
        onItemAdded()
    }
    const handleUpdateItemLineAtIndex = (index: number, newLine: Line) => {
        const lineToUpdate = itemLines[index]
        if (lineToUpdate === undefined) {
            return
        }

        const newSalesTaxInfo = newLine.taxable
            ? {
                  nexus: 'pro',
                  taxcode: owner.taxcode,
                  tax: owner.taxcode?.rate,
              }
            : {}

        onUpdateInvoice(
            invoice.clone({
                lines: invoice.lines.map((line) => (line === lineToUpdate ? newLine : line)),
                ...newSalesTaxInfo,
            })
        )
    }
    const handleRemoveItemLineAtIndex = (index: number) => {
        const lineToRemove = itemLines[index]
        if (lineToRemove === undefined) {
            return
        }
        onUpdateInvoice(
            invoice.clone({
                lines: invoice.lines.filter((line) => line !== lineToRemove),
            })
        )
    }

    const handleAddExpense = () => {
        const expenseLine = Line.initWithType('expense', invoice)
        expenseLine.total = 0
        expenseLine.quantity = 1
        expenseLine.taxable = owner.allowSalesTax
        expenseLine.discountable = owner.allowDiscount

        setCurrentEditingExpenseLine({ type: 'new', line: expenseLine })
    }
    const handleUpdateExpenseLineAtIndex = (index: number, newLine: Line) => {
        const lineToUpdate = expenseLines[index]
        if (lineToUpdate === undefined) {
            return
        }
        onUpdateInvoice(
            invoice.clone({
                lines: invoice.lines.map((line) => (line === lineToUpdate ? newLine : line)),
            })
        )
    }
    const handleRemoveExpenseLineAtIndex = (index: number) => {
        const lineToRemove = expenseLines[index]
        if (lineToRemove === undefined) {
            return
        }
        onUpdateInvoice(
            invoice.clone({
                lines: invoice.lines.filter((line) => line !== lineToRemove),
            })
        )
    }

    const bookingItems = useMemo(() => {
        if (clientBookings === null || clientBookings === undefined) {
            return null
        }

        return clientBookings.map((booking) => ({
            id: booking.id ?? '',
            title: moment(booking.date).format('ddd, MMMM Do, YYYY'),
            subtitle:
                booking.item?.name ??
                `${moment(booking.date).format('h:mm A')} - ${moment(booking.endDate).format('h:mm A')}`,
            thirdLine: <AppointmentItemThirdLine booking={booking} />,
            secondaryTitle: booking.rate !== undefined ? currencyFormat(booking.rate, true) : 'TBD',
            booking,
        }))
    }, [clientBookings])
    const selectedBookingItems = useMemo(() => {
        if (bookingItems === null) {
            return []
        }

        const selectedBookingIDs = new Set(
            compact(appointmentLines.map((line) => line.booking?.id))
        )
        return bookingItems.filter((item) => selectedBookingIDs.has(item.booking.id ?? ''))
    }, [bookingItems, appointmentLines])
    const handleSelectBookingItems = (items: { id: string; booking: Booking }[]) => {
        const newAppointmentLines = items.map((item) => Line.initWithBooking(item.booking, invoice))
        const otherLines = invoice.lines.filter((line) => line.type !== 'booking')
        onUpdateInvoice(invoice.clone({ lines: [...otherLines, ...newAppointmentLines] }))
    }
    const handleRemoveAppointmentLineWithBookingID = (bookingID: string) => {
        onUpdateInvoice(
            invoice.clone({ lines: invoice.lines.filter((line) => line.booking?.id !== bookingID) })
        )
    }
    const appointmentLineBookings = compact(appointmentLines.map((line) => line.booking))

    const receipt = useMemo((): Receipt => {
        return {
            modifiers: {
                subtotal: {
                    amount: invoice.total ?? 0,
                    onUpdate:
                        itemLines.length === 0 &&
                        expenseLines.length === 0 &&
                        appointmentLines.length === 0
                            ? (newAmount) => onUpdateInvoice(invoice.clone({ total: newAmount }))
                            : undefined,
                },
                giftCertificate: owner.allowGift
                    ? {
                          selectedGift: invoice.gift,
                          onSelectGift: (gift) => {
                              if (gift !== undefined) {
                                  onUpdateInvoice(
                                      invoice.clone({
                                          gift,
                                          discount_type: 'rate',
                                          discount: gift.balance ?? 0,
                                      })
                                  )
                              }
                            else {
                                  onUpdateInvoice(invoice.clone({ gift: undefined }))
                              }
                          },
                      }
                    : undefined,
                discount: owner.allowDiscount ? {} : undefined,
                salesTax: owner.allowSalesTax
                    ? {
                          amount: invoice.taxAmount ?? null,
                          parenthetical:
                              invoice.taxcode !== undefined
                                  ? `${invoice.taxcode.rate ?? 0}%`
                                  : null,
                      }
                    : undefined,
                surcharge: owner.allowSurcharge
                    ? {
                          amount: invoice.allowSurcharge ? invoice.surchargeAmount : null,
                          onAdd: () => onUpdateInvoice(invoice.clone({ allow_surcharge: true })),
                          onRemove: () =>
                              onUpdateInvoice(invoice.clone({ allow_surcharge: false })),
                      }
                    : undefined,
            },
            total: invoice.chargeAmount,
        }
    }, [invoice, itemLines, expenseLines, appointmentLines])

    const handlePaymentPlanChange = (option: { id: string } | null) => {
        const newInvoice = invoice.clone()
        newInvoice.allowAnyPayment = option === null || option.id === '' ? undefined : option.id
        onUpdateInvoice(newInvoice)
    }

    return (
        <div className="mt-12 flex flex-col items-center justify-start gap-y-6">
            <WorkflowPage.Card title="General">
                <WorkflowPage.IconSection title="Client (required)" icon="user.large">
                    <ClientSearchableSelect
                        mode="server"
                        selectedClient={invoice.client}
                        selectedSubaccount={invoice.subaccount}
                        onSelectClient={handleSelectClient}
                        onSelectSubaccount={handleSelectSubaccount}
                        shouldPreserveLastSelection={true}
                        canAddNew
                        shouldIncludeSubaccounts
                    />
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection
                    title="Items"
                    icon="listPlus.large"
                    secondaryText={isItemAddedBadgeShowing ? 'Added' : undefined}
                    secondaryTextClassName="font-medium text-success-400"
                >
                    <SearchableSelect
                        items={itemsSearchableItems}
                        onSelectItem={handleSelectItemOption}
                        titleWeight="bold"
                        placeholder="Type to search"
                        shouldRefocusAfterSelection
                    />

                    {itemLines.map((line, lineIndex) => (
                        <WorkflowPage.ItemLine
                            key={lineIndex}
                            line={line}
                            onUpdate={(newLine) => handleUpdateItemLineAtIndex(lineIndex, newLine)}
                            onRemove={() => handleRemoveItemLineAtIndex(lineIndex)}
                        />
                    ))}
                </WorkflowPage.IconSection>

                <WorkflowPage.IconSection
                    title="Expenses"
                    icon="coins.large"
                    description="Select expenses to include"
                    secondaryView={
                        <Button
                            onClick={handleAddExpense}
                            intent="tertiaryLink"
                            className="mr-2 translate-x-3 hover:opacity-100"
                            size="small"
                        >
                            Select
                        </Button>
                    }
                    hasHoverEffect
                >
                    {expenseLines.length > 0 && (
                        <div className="flex flex-col items-stretch justify-start gap-y-3">
                            {expenseLines.map((line, lineIndex) => (
                                <WorkflowPage.ExpenseLine
                                    key={lineIndex}
                                    line={line}
                                    onUpdate={(newLine) =>
                                        handleUpdateExpenseLineAtIndex(lineIndex, newLine)
                                    }
                                    onRemove={() => handleRemoveExpenseLineAtIndex(lineIndex)}
                                    onEdit={() =>
                                        setCurrentEditingExpenseLine({
                                            type: 'edit',
                                            line,
                                            lineIndex,
                                        })
                                    }
                                />
                            ))}
                        </div>
                    )}
                </WorkflowPage.IconSection>

                {currentEditingExpenseLine !== null && (
                    <WorkflowPage.EditExpenseLineModal
                        isOpen
                        onClose={() => setCurrentEditingExpenseLine(null)}
                        onDone={(line) => {
                            switch (currentEditingExpenseLine.type) {
                                case 'new': {
                                    onUpdateInvoice(
                                        invoice.clone({ lines: [...invoice.lines, line] })
                                    )
                                    break
                                }
                                case 'edit': {
                                    handleUpdateExpenseLineAtIndex(
                                        currentEditingExpenseLine.lineIndex,
                                        line
                                    )
                                    break
                                }
                            }
                            setCurrentEditingExpenseLine(null)
                        }}
                        line={currentEditingExpenseLine.line}
                    />
                )}

                <WorkflowPage.IconSection
                    title="Appointments"
                    icon="calendar.large"
                    description="Select date and time"
                    secondaryView={
                        <div className="flex items-center justify-end">
                            {areClientBookingsLoading && <Spinner size={24} />}

                            <Button
                                onClick={() => {
                                    if (bookingItems === null || bookingItems.length === 0) {
                                        setIsNoAppointmentsModalOpen(true)
                                    }
                                    else {
                                        setIsBookingsSelectModalOpen(true)
                                    }
                                }}
                                intent="tertiaryLink"
                                className="mr-2 translate-x-3 hover:opacity-100"
                                size="small"
                            >
                                {appointmentLineBookings.length === 0 ? 'Select' : 'Edit'}
                            </Button>
                        </div>
                    }
                    hasHoverEffect
                >
                    {appointmentLineBookings.length > 0 && (
                        <div className="flex flex-col items-stretch justify-start gap-y-3">
                            {appointmentLineBookings.map((booking) => (
                                <WorkflowPage.RemovableItemContainer
                                    key={booking.id ?? ''}
                                    onRemove={() =>
                                        handleRemoveAppointmentLineWithBookingID(booking.id ?? '')
                                    }
                                >
                                    <WorkflowPage.SelectedItem
                                        title={moment(booking.date).format('ddd, MMMM Do')}
                                        subtitle={`${moment(booking.date).format('h:mm A')} - ${moment(booking.endDate).format('h:mm A')}`}
                                        thirdLine={<AppointmentItemThirdLine booking={booking} />}
                                    />
                                </WorkflowPage.RemovableItemContainer>
                            ))}
                        </div>
                    )}
                </WorkflowPage.IconSection>

                {bookingItems !== null && (
                    <SelectModal
                        isOpen={isBookingsSelectModalOpen}
                        onClose={() => setIsBookingsSelectModalOpen(false)}
                        selectionType="multi"
                        items={bookingItems}
                        selectedItems={selectedBookingItems}
                        onSelectItems={handleSelectBookingItems}
                        title="Select Appointments"
                    />
                )}
            </WorkflowPage.Card>

            <WorkflowPage.Card title="Total">
                <div>
                    <WorkflowPage.ReceiptView
                        receipt={receipt}
                        transaction={invoice}
                        onUpdateTransaction={onUpdateInvoice}
                        client={invoice.client}
                        owner={owner}
                    />

                    <Divider />

                    <div className="flex items-stretch justify-center gap-x-4">
                        <div className="flex shrink-0 items-start justify-center">
                            <Icon icon="cards.large" className="text-gray-500" />
                        </div>
                        <div className="grow">
                            <Paragraph size="md">Payment plan</Paragraph>
                            <Paragraph className="text-paragraph-sm mt-1 text-gray-600">
                                Specify the max number of payments for this invoice and the minimum
                                payment amount. Note that your clients can opt to pay the full
                                invoice balance at any time.
                            </Paragraph>
                        </div>
                        <div className="flex shrink-0 items-center justify-center">
                            <SearchableSelect
                                items={paymentPlanDropdownOptions}
                                selectedItemID={invoice.allowAnyPayment ?? ''}
                                onSelectItem={handlePaymentPlanChange}
                                size="medium"
                                placeholder="None"
                                align="end"
                                shouldPreserveLastSelection
                            />
                        </div>
                    </div>

                    <Divider />

                    <div className="flex items-stretch justify-center gap-x-4">
                        <div className="flex shrink-0 items-start justify-center">
                            <Icon icon="calendar.large" className="text-gray-500" />
                        </div>
                        <div className="grow">
                            <Paragraph size="md">Due date</Paragraph>
                            <Paragraph className="text-paragraph-sm mt-1 text-gray-600">
                                Specify when payment for this invoice is due.
                            </Paragraph>
                        </div>
                        <div className="flex shrink-0 items-center justify-center">
                            <DatePicker
                                size="medium"
                                placeholder="Select due date"
                                minDateString={moment().format('MM/DD/YYYY')}
                                selectedDateString={moment(invoice.due_date).format('MM/DD/YYYY')}
                                onSelectDate={(newDate) =>
                                    onUpdateInvoice(invoice.clone({ due_date: moment([newDate.getFullYear(), newDate.getMonth(), newDate.getDate()]) }))
                                }
                            />
                        </div>
                    </div>
                </div>
            </WorkflowPage.Card>

            <WorkflowPage.NotificationsSection
                transaction={invoice}
                onUpdateTransaction={onUpdateInvoice}
                sendViaDescription="Choose how you'd like your client to be sent this invoice. Clients can pay this invoice online - from their smartphone or computer."
                reminderDescription="If an invoice remains unpaid, chose how often you'd like your client to be politely reminded until payment is received."
            />

            <WorkflowPage.Card title="More options" defaultIsCollapsed>
                <WorkflowPage.IconSection
                    title="Memo"
                    icon="note.large"
                    secondaryText={`${(invoice.memo ?? '').length} / 1500`}
                >
                    <TextArea
                        size="medium"
                        isAutoResizing
                        placeholder="Add a memo"
                        className="overlow-y-hidden"
                        value={invoice.memo ?? ''}
                        onChange={(event) =>
                            onUpdateInvoice(invoice.clone({ memo: event.target.value }))
                        }
                        maxLength={1500}
                        onClear={() => onUpdateInvoice(invoice.clone({ memo: '' }))}
                    />
                </WorkflowPage.IconSection>

                {owner.allowRecord && (
                    <WorkflowPage.IconSection title="Attach a form" icon="field.large">
                        <TypeSearchableSelect
                            owner={owner}
                            selectedForm={invoice.form}
                            onSelectForm={(form) => onUpdateInvoice(invoice.clone({ form }))}
                        />
                    </WorkflowPage.IconSection>
                )}

                {owner.allowContract && (
                    <WorkflowPage.IconSection title="Attach a contract" icon="pen.large">
                        <ContractSearchableSelect
                            selectedContract={invoice.contract}
                            onSelectContract={(contract) =>
                                onUpdateInvoice(invoice.clone({ contract }))
                            }
                        />
                    </WorkflowPage.IconSection>
                )}
            </WorkflowPage.Card>

            <Modal
                isOpen={isNoAppointmentsModalOpen}
                onClose={() => setIsNoAppointmentsModalOpen(false)}
                title="Appointments"
                footer={
                    <Button intent="primary" onClick={() => setIsNoAppointmentsModalOpen(false)}>
                        Done
                    </Button>
                }
            >
                <div className="flex flex-col items-center justify-center">
                    <img src={PencilChecklistPersonImageSrc} width={100} height={100} />
                    <Heading size="xs" className="mt-4">
                        No appointments
                    </Heading>
                    <Label size="md" className="mt-2 font-normal text-gray-600">
                        There are no appointments to invoice
                    </Label>
                </div>
            </Modal>
        </div>
    )
}

const paymentPlanDropdownOptions = Invoice.allowAnyPaymentStrings().map((option) => ({
    id: option.id ?? '',
    title: option.label ?? '',
}))

const AppointmentItemThirdLine = ({ booking }: { booking: Booking }) => {
    const iconConfigs = compact(booking.services.map((service) => service.iconConfig))
    const numVisibleImages = iconConfigs.length > 5 ? 4 : iconConfigs.length
    const numExtraServices = iconConfigs.length - numVisibleImages

    return (
        <div className="flex items-center justify-start gap-x-2">
            {iconConfigs.length > 0 && (
                <div
                    className="grid grid-rows-1"
                    style={{
                        gridTemplateColumns: `repeat(${1 + numVisibleImages + (numExtraServices > 0 ? 1 : 0)}, 14px)`,
                    }}
                >
                    {take(iconConfigs, numVisibleImages).map((iconConfig, iconIndex) =>
                        iconConfig.type === 'image' ? (
                            <img
                                key={iconIndex}
                                src={iconConfig.uri}
                                className="col-span-2 row-start-1 size-7 rounded-full border-2 border-white"
                                style={{
                                    gridColumnStart: iconIndex + 1,
                                    gridColumnEnd: iconIndex + 3,
                                }}
                            />
                        ) : (
                            <Icon
                                key={iconIndex}
                                icon={iconConfig.icon}
                                background="round"
                                className="col-span-2 row-start-1 size-7 border-2 border-white"
                                style={{
                                    gridColumnStart: iconIndex + 1,
                                    gridColumnEnd: iconIndex + 3,
                                }}
                            />
                        )
                    )}

                    {numExtraServices > 0 && (
                        <div
                            className="col-span-2 row-start-1 flex size-7 items-center justify-center rounded-full bg-gray-200 text-gray-600"
                            style={{
                                gridColumnStart: numVisibleImages + 1,
                                gridColumnEnd: numVisibleImages + 3,
                            }}
                        >
                            +{numExtraServices}
                        </div>
                    )}
                </div>
            )}

            <Label className="truncate font-light text-gray-600" size="sm">
                {compact(booking.services.map((service) => service.name)).join(', ')}
            </Label>
        </div>
    )
}
